/*
 * Copyright 2022 Oriflame
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { CompoundEntityRef } from '@backstage/catalog-model';

export enum ScoreSuccessEnum {
  Success = 'success',
  Partial = 'partial',
  Failure = 'failure',
}

/**
 * What a score was actually computed over (feature 0033).
 *
 * The units differ by level, deliberately: on a metric entry they are
 * **projects** (`applicable` = the repos the profile scores that metric on,
 * `measured` = the repos that contributed to its `scorePercent`), while on an
 * area or a pillar they are **checks** — one metric evaluated for one repo —
 * i.e. the element-wise sum of the level below. A pillar reading
 * `{measured: 37, applicable: 38}` therefore means 37 of 38 metric-repo pairs
 * were known, not 37 repos.
 *
 * `measured` is the score's own denominator, never a second opinion about it:
 * a repo excluded as uncollected (feature 0024's `-1`) is the only kind of gap,
 * while a repo with no stored row at all still contributes its 0 and counts as
 * measured. Coverage and score can therefore never contradict each other.
 */
export interface MetricCoverage {
  measured: number;
  applicable: number;
  /**
   * Metric entries only (feature 0050): how many of the row's own units the
   * metric does not apply to (their profile excludes it). Counted in the row's
   * unit — repositories on a repository-scoped row, Components on a
   * component-scoped one — so that `applicable + notApplicable + unprofiled`
   * is that unit's System total. Absent from a pre-0050 IMC and on rollups.
   */
  notApplicable?: number;
  /**
   * Metric entries only (feature 0050): how many of the row's own units have no
   * usable profile. Same unit and invariant as {@link notApplicable}.
   */
  unprofiled?: number;
}

/**
 * A repo named on a metric row without any score attached (feature 0033) —
 * used for the repos nothing could be collected for. Deliberately NOT a
 * {@link FailingProject}: a `score` or `label` would be a fabrication for a
 * repo nothing was measured on. `projectKey` is the GitLab path slug, resolved
 * to a catalog Component exactly as the failing list resolves its own.
 */
export interface ProjectRef {
  projectKey: string;
  name: string;
  /**
   * The Backstage Component this row is about (feature 0050), e.g.
   * `component:default/elevare-app-frontend`. IMC sends it on
   * component-scoped metric rows only; it is what disambiguates a repository
   * backing several Components, where {@link projectKey} alone is ambiguous.
   * Absent/null on repository-scoped rows and from a pre-0050 IMC.
   */
  entityRef?: string | null;
  /**
   * Why the metric could not be collected for this repo (feature 0028), e.g.
   * `trigger job 'deploy' starts a multi-project pipeline (trigger: project) —
   * not inspected`. Mirrors IMC's `ProjectRefDto.reason`; set only on entries
   * of `uncollectedProjects`, and omitted from the JSON when IMC has none.
   */
  reason?: string;
}

export interface EntityScore {
  entityRef: CompoundEntityRef;
  generatedDateTimeUtc: Date | string;
  /**
   * The System's overall (Total) score, 0–100. `null` when no pillar is
   * measured — e.g. every repo in the System is unprofiled (feature 0017), so
   * the Engineering pillar drops out and there is nothing to score. A null
   * overall must render as "not scored", never coerced to 0/red.
   */
  scorePercent: number | null;
  scoreLabel?: string;
  scoreSuccess: ScoreSuccessEnum | null;
  scoringReviewer: string | CompoundEntityRef | undefined | null;
  scoringReviewDate: Date | string | undefined | null;
  pillars: EntityScorePillar[];
  /**
   * How many repos the System contains, and how many of those carry no usable
   * metric profile (feature 0033). Both are System-wide constants, carried once
   * here rather than repeated on every entry, and together they let a metric row
   * explain its own denominator:
   *
   * ```
   * notApplicable = projectCount − unprofiledProjectCount − coverage.applicable
   * ```
   *
   * The split exists because IMC's applicability test rejects a repo for two
   * different reasons — its profile excludes the metric, or it has no profile at
   * all (feature 0017: "unprofiled repos are not scored") — and only the second
   * routes an action. IMC pins the invariant that the subtraction never goes
   * negative and always reconciles to `projectCount`.
   *
   * Optional because a pre-0033 IMC omits both; absent therefore means "cannot
   * state how much of the fleet this covers", not zero.
   */
  projectCount?: number;
  /** See {@link projectCount}. */
  unprofiledProjectCount?: number;
  /**
   * radar-backend only: the project's display name when it has no catalog
   * System (its row is radar's alone). A catalog title always wins.
   */
  title?: string;
  /** radar-backend only: whether the project fits what radar measures today. */
  setup?: ProjectSetup;
}

/**
 * radar-backend's label for a project: `ready` = measured as designed,
 * `limited` = measured with known gaps (`notes` says which).
 */
export interface ProjectSetup {
  status: 'ready' | 'limited';
  notes: string[];
}

/**
 * A top-level quality pillar (global area) grouping a set of areas.
 *
 * Only measured pillars carry a score. An unmeasured pillar is a declared
 * placeholder: `measured: false`, no score at all, and (except for feature
 * 0025's `no-production-accounts` Security case) no areas either. `measured` is
 * therefore the field to branch on — never the presence of a score.
 */
export interface EntityScorePillar {
  id: number;
  key: string;
  title: string;
  measured: boolean;
  /**
   * The pillar's 0–100 score. ABSENT — not null — on an unmeasured pillar: IMC
   * builds those with a null score and serialises with a global
   * `WhenWritingNull`, so the key never reaches the wire. Declared
   * `?: number | null` rather than `?: number` because older stored payloads
   * (and the plugin's non-IMC JSON sources) do write an explicit null, and both
   * must read as "no score".
   */
  scorePercent?: number | null;
  /** Absent whenever {@link scorePercent} is — see there. */
  scoreSuccess?: ScoreSuccessEnum | null;
  // Present on the detailed per-System contract (pillar → areas → entries);
  // absent on the simplified board contract (IMC's SimplifiedPillarScoreDto),
  // which carries only pillar-level score. Optional to model both.
  areaScores?: EntityScoreArea[];
  /**
   * Why an unmeasured pillar has no score (feature 0025) — so the Security tab
   * can state it in words instead of a bare `n/a`, which is indistinguishable
   * from "the collector hasn't run yet".
   *
   * IMC omits the key entirely when the pillar IS measured, and also for a
   * pillar that is simply unmeasured because it has no collectors yet
   * (Product / Delivery) — hence optional, not `| null`.
   */
  unmeasuredReason?: PillarUnmeasuredReason;
  /**
   * Why a MEASURED pillar's score does not cover everything the pillar claims to
   * measure (feature 0037 / ADR-0022) — {@link unmeasuredReason}'s counterpart,
   * and mutually exclusive with it: `unmeasuredReason` means nothing scored,
   * `partialReason` means something did but not all of it.
   *
   * The Security pillar became hybrid — an entry-based Code Security area beside
   * two account-scoped ones — so "no production account curated" no longer blanks
   * the whole pillar: the code area still scores it, and this names what the
   * score therefore leaves out. Deliberately NOT emitted the other way round
   * (code area missing, cloud areas scored): that gap is already stated by
   * `SonarQube Integration` and the area's own coverage.
   *
   * Same closed vocabulary as {@link unmeasuredReason}. Carried on BOTH shapes of
   * this type — the detailed pillar (IMC's `PillarScoreDto`) and the board pillar
   * (`SimplifiedPillarScoreDto`) — because the board is where the number is read
   * with no areas and no banner around it. IMC omits the key when null, hence
   * optional rather than `| null`.
   */
  partialReason?: PillarUnmeasuredReason;
  /**
   * What this pillar's score was computed over, in **checks** (feature 0033) —
   * see {@link MetricCoverage}.
   *
   * ABSENT, not zero, on the Security pillar and on any pillar that has no
   * metric entries: Security states what it scored through `scored` per account
   * plus {@link unmeasuredReason}/{@link partialReason} instead (features 0025,
   * 0037), and Product/Delivery have no collectors yet. A hybrid pillar
   * deliberately does not roll its one entry-based area's coverage up here,
   * which would present that area's denominator as the whole pillar's.
   * Absence is the model, so branch on the field being
   * present — a zeroed coverage would claim "nothing of everything was measured",
   * which is a different and false statement.
   */
  coverage?: MetricCoverage;
  /**
   * Delivery pillar from radar-backend only: one point per closed sprint, oldest
   * first, behind the sprint plan and bug trend charts (metrics tracker #15,
   * #17, #18). Absent from IMC and from the board contract.
   */
  sprintHistory?: SprintHistoryPoint[];
  /**
   * Delivery from radar-backend only: how the team plans work. Kanban has no
   * sprints, so its sprint metrics (#16–#18) are n/a.
   */
  cadence?: 'scrum' | 'kanban';
  /**
   * Delivery from radar-backend only: the System's Jira scrum boards, when its
   * team runs more than one — the Delivery tab's board filter options.
   */
  boards?: string[];
  /** Delivery from radar-backend only: the boards this pillar covers; absent = all of them. */
  selectedBoards?: string[];
  /** Delivery from radar-backend only: #14 median vs mean days to resolve, overall then per severity. */
  bugResolution?: ResolutionTimePoint[];
  /** Delivery from radar-backend only: #16 when each story of the latest closed sprint reached QA. */
  qaHandoffTimeline?: QaHandoffTimeline;
  /** Delivery from radar-backend only: #10 open bugs per age bucket, youngest first. */
  openBugAges?: OpenBugAgeBucket[];
  /** Delivery from radar-backend only: #11 open bugs per canonical severity level. */
  openBugSeverity?: OpenBugSeverity;
  /** Delivery from radar-backend only: #11.1 open bugs per Jira Priority, highest first. */
  openBugPriority?: OpenBugPriorityPoint[];
  /** Delivery from radar-backend only: #11.2 open bugs by Priority (rows) × Severity (columns). */
  openBugMatrix?: OpenBugMatrix;
  /** Delivery from radar-backend only: #12 bugs closed in the window and how many were reopened. */
  reopenRate?: { closed: number; reopened: number };
  /** Product from radar-backend only: #1 completed stories and how many have a linked case. */
  storyCoverage?: { covered: number; total: number };
  /** Product from radar-backend only: #2 cases per top-level TestRail section, worst first. */
  automationBySection?: SectionAutomationPoint[];
  /** Product from radar-backend only: #3 status counts of each regression run shown. */
  regressionRuns?: RegressionRunPoint[];
  /**
   * Product from radar-backend only, when no automated regression filter is
   * configured: every run and test plan of the last 30 days, newest first.
   */
  recentRuns?: RecentRunGroupPoint[];
  /** Product from radar-backend only: #2 per TestRail suite, worst first. */
  automationBySuite?: SuiteAutomationPoint[];
}

/** #2 per TestRail suite, each with its top-level sections. */
export interface SuiteAutomationPoint {
  suiteId: number | null;
  suite: string;
  total: number;
  automated: number;
  notAutomatable: number;
  percent: number | null;
  sections: SectionAutomationPoint[];
  /** Every section with its own (direct) cases; absent on older payloads. */
  tree?: SectionNodePoint[];
}

/** One TestRail section and the cases filed directly in it (not in its sub-sections). */
export interface SectionNodePoint {
  sectionId: number | null;
  /** Null for a top-level section. */
  parentId: number | null;
  name: string;
  total: number;
  automated: number;
  notAutomatable: number;
}

export interface RecentRunPoint {
  runId: number;
  runName: string;
  /** ISO timestamp. */
  createdAt: string;
  passed: number;
  failed: number;
  blocked: number;
  retest: number;
  untested: number;
  /** The fields below are absent on payloads from before radar-backend counted them. */
  inProgress?: number;
  skipped?: number;
  timedout?: number;
  /** Passed on a Playwright retry. */
  flaky?: number;
}

/** A test plan with its runs, or one standalone run (planId null). */
export interface RecentRunGroupPoint {
  planId: number | null;
  planName: string | null;
  runs: RecentRunPoint[];
}

export interface OpenBugAgeBucket {
  /** "0–7 d", "8–30 d", "31–90 d" or "90+ d". */
  bucket: string;
  count: number;
}

export interface OpenBugSeverity {
  critical: number;
  major: number;
  minor: number;
  /** Open bugs whose Severity is empty or outside the canonical mapping. */
  unset: number;
}

export interface OpenBugMatrix {
  /** Raw Severity values, most severe first, then "No severity". */
  severities: string[];
  /** Raw Priority values, highest first, then "No priority". */
  priorities: string[];
  /** counts[priority][severity]. */
  counts: number[][];
  /** urls[priority][severity]: that cell's open bugs as a Jira search. */
  urls?: string[][];
}

export interface OpenBugPriorityPoint {
  /** The Jira Priority name, or "No priority". */
  priority: string;
  total: number;
  /** The same bugs by canonical severity. */
  critical: number;
  major: number;
  minor: number;
  unset: number;
}

export interface SectionAutomationPoint {
  section: string;
  total: number;
  automated: number;
  notAutomatable: number;
  /** Null when every case in the section can't be automated. */
  percent: number | null;
}

export interface RegressionRunPoint {
  /** The #3 metric row this run scores; absent on older payloads. */
  entryId?: number;
  runId: number;
  runName: string;
  passed: number;
  failed: number;
  blocked: number;
  retest: number;
  untested: number;
  /** The fields below are absent on payloads from before radar-backend counted them. */
  inProgress?: number;
  skipped?: number;
  timedout?: number;
  /** Passed on a Playwright retry. */
  flaky?: number;
}

export interface ResolutionTimePoint {
  /** "all", then each raw Jira Severity (e.g. "Blocker"), then "No severity". */
  level: string;
  medianDays: number;
  meanDays: number;
  count: number;
  /** Spread of days-to-resolve; absent on older payloads. */
  minDays?: number;
  p25Days?: number;
  p75Days?: number;
  maxDays?: number;
}

export interface QaHandoffTimeline {
  sprintName: string;
  /** ISO timestamps. */
  startDate: string | null;
  endDate: string;
  /** Hand-offs from here on count as late. */
  lateFrom: string;
  handoffs: Array<{
    key: string;
    summary?: string | null;
    /** The story in Jira; older payloads have none. */
    url?: string;
    at: string;
    late: boolean;
  }>;
}

export interface SprintHistoryPoint {
  sprintName: string;
  /** ISO date (YYYY-MM-DD) the sprint (or kanban window) started; absent on older payloads. */
  startDate?: string | null;
  /** ISO date (YYYY-MM-DD) the sprint ended. */
  endDate: string | null;
  /** Story points of everything in the sprint when it closed. */
  planned: number;
  /** Story points Done when the sprint closed. */
  completed: number;
  /** The team's usual planned SP at that sprint; null with fewer than 6 earlier sprints. */
  usual: number | null;
  bugsCreated: number;
  bugsClosed: number;
  /** Of the bugs closed in the sprint, those reopened at least once (#12); absent on older payloads. */
  bugsReopened?: number;
  /** Bugs open when the sprint ended (#10 trend); absent on older payloads. */
  openBugsAtEnd?: number | null;
  /** #16 per sprint: stories handed to QA and how many late; older payloads have none. */
  handoffs?: number;
  lateHandoffs?: number;
  /** #16: the late stories, in hand-off order. */
  lateStories?: LateStory[];
  /** #16: all of them in one Jira search (absent when too many for a URL). */
  lateStoriesUrl?: string;
}

export interface LateStory {
  key: string;
  summary: string | null;
  /** Whole days between the hand-off and the sprint's end (0 = its last day). */
  daysBeforeEnd: number;
  url?: string;
}

/**
 * The reasons a pillar can report for a missing score (feature 0025).
 * `no-data` — nothing was collected at all, so `areaScores` is empty.
 * `no-production-accounts` — the System curates accounts but none is
 * production, so nothing could be scored; `areaScores` IS populated, because
 * those unscored rows are the explanation for the missing score.
 *
 * One closed vocabulary, two fields: {@link EntityScorePillar.unmeasuredReason}
 * states it for a pillar that scored nothing, {@link
 * EntityScorePillar.partialReason} for a hybrid pillar that scored some of its
 * areas and not others (feature 0037). Deliberately not forked into a second
 * union — the reason is the same fact either way; only how much it blanks
 * differs.
 */
export type PillarUnmeasuredReason = 'no-production-accounts' | 'no-data';

export interface EntityScoreArea {
  id: number;
  title: string;
  /**
   * The area's 0–100 score. Absent OR explicitly null on an account-scoped Security area whose
   * accounts were all left out of the score (feature 0025): the area still
   * carries its non-production rows, but nothing in it was scored. That is the
   * case whether the pillar is unmeasured for `no-production-accounts` or
   * measured from its Code Security area alone (feature 0037 — see
   * {@link EntityScorePillar.partialReason}). Every other area always carries it.
   */
  scorePercent?: number | null;
  scoreLabel?: string;
  /** Absent or null whenever {@link scorePercent} is — see there. */
  scoreSuccess?: ScoreSuccessEnum | null;
  scoreEntries: EntityScoreEntry[];
  // Per-account finding counts, populated only for Security-pillar areas
  // (feature 0014). Each row is one of the System's curated AWS accounts with
  // its Critical/High/Medium finding counts for this area (a security index).
  // Additive to the 0–100 score model; the SecurityPillarView renders these as
  // the per-account table in place of metric scoreEntries.
  accountFindings?: SecurityAccountFinding[];
  /**
   * What this area's score was computed over, in **checks** (feature 0033) — the
   * element-wise sum of its entries' coverage. Carried by every ENTRY-based
   * area, including the Security pillar's Code Security area (feature 0037): it
   * is scored per repo, so "measured on 4 of 6 repos" is meaningful there.
   * Absent on the account-scoped Security areas, for the same reason it is
   * absent on the Security pillar itself; see {@link EntityScorePillar.coverage}.
   */
  coverage?: MetricCoverage;
}

/** The environment IMC classified a curated AWS account into (feature 0025). */
export type AccountEnvironment = 'production' | 'non-production' | 'unknown';

/**
 * Per-account severity counts for a Security-pillar area (feature 0014).
 * `accountId` is the bare AWS account id (matches the `aws-account-<id>`
 * catalog Resource / `temabit.com/aws-account-id` annotation).
 */
export interface SecurityAccountFinding {
  accountId: string;
  critical: number;
  high: number;
  medium: number;
  /**
   * The account's classified environment (feature 0025). Carried for
   * completeness — the view never splits on it, see {@link scored}.
   */
  environment?: AccountEnvironment;
  /**
   * Whether the score IMC reported actually used this account (feature 0025).
   *
   * This — not `environment`, and not a client-side `spec.lifecycle` lookup — is
   * what the Security table splits on. The two are genuinely different: in the
   * legacy branch (nothing classified) every account is scored while its
   * `environment` is `unknown`. Splitting on anything else means two
   * classifiers that can drift, and the whole point is that what the table
   * shows is what the score used.
   *
   * Optional because a pre-0025 IMC omits it; absent therefore means "scored",
   * which is exactly how that IMC behaved.
   */
  scored?: boolean;
}

export interface FailingProject {
  projectKey: string;
  name: string;
  score: number | null;
  /**
   * The project's own grade for this metric, when a percentage would be
   * meaningless (feature 0032). Present for rating metrics — the SonarCloud
   * letter, e.g. `"D"` — where IMC leaves {@link score} null and sends the
   * letter instead; absent for boolean and percent metrics.
   */
  label?: string;
  /** The Component this row is about (feature 0050) — see {@link ProjectRef.entityRef}. */
  entityRef?: string | null;
}

export interface EntityScoreEntry {
  id: number;
  title: string;
  isOptional: boolean;
  /**
   * Null when the source has too little history to score the metric yet
   * (radar-backend: e.g. Sprint Plan Overload with < 6 earlier sprints). The
   * row renders as n/a and the source leaves it out of every roll-up.
   */
  scorePercent: number | null;
  scoreLabel?: string;
  scoreSuccess: ScoreSuccessEnum | null;
  scoreHints: string | string[];
  details: string;
  weight: number;
  /**
   * What the metric measures and how (formula, thresholds) — radar-backend
   * sends one per row; shown as an info tooltip beside the title.
   */
  description?: string;
  failingProjects?: FailingProject[];
  guidelineUrl?: string;
  /**
   * What this metric's score was computed over, in **projects** (feature 0033) —
   * see {@link MetricCoverage}. `measured` of `applicable`; anything applicable
   * but not measured is named in {@link uncollectedProjects}.
   *
   * Optional because a pre-0033 IMC omits it, in which case the row renders
   * exactly as it did before this feature — with no coverage line at all rather
   * than a fabricated "0 of 0".
   */
  coverage?: MetricCoverage;
  /**
   * The applicable repos nothing could be collected for (feature 0033), named so
   * the gap is investigable rather than merely counted — a count cannot be
   * clicked through to a repo. Ordered by name, case-insensitively, by IMC.
   *
   * Always emitted by a 0033-or-newer IMC (`[]` when there is no gap), so an
   * empty array and an absent field mean different things: no gap, versus an IMC
   * too old to say. Rendered like the existing "Failing:" line, and the two lists
   * partition the same applicable set: `failingProjects` ⊆ measured,
   * `uncollectedProjects` = applicable − measured.
   */
  uncollectedProjects?: ProjectRef[];
}

export interface EntityScoreExtended extends EntityScore {
  id: string;
  owner: CompoundEntityRef | undefined;
  reviewer: CompoundEntityRef | undefined;
  reviewDate: Date | undefined;
  // The catalog entity's display title (e.g. "BitCore"), resolved from the
  // catalog so the board can show the same name as the entity page header.
  // Falls back to the raw entityRef name when unavailable.
  title?: string;
  // The System's business Domain (`spec.domain`), resolved from the catalog —
  // the axis the board's Domain scope filters on (feature 0039). Absent, not
  // null, when the System declares no domain: most Systems carry none while
  // the per-repo mapping rolls out, and the Domain control only appears once
  // at least one score has this field.
  domain?: string;
  /**
   * False when no catalog entity matches the row — a radar project without a
   * System. Its score card lives on the board (`/score-board/project/<name>`)
   * instead of the catalog's Score tab. Absent = unknown (treated as present).
   */
  inCatalog?: boolean;
}

// --- Score History types (feature 0004) ---

export interface ScoreHistoryResponse {
  entityRef: CompoundEntityRef;
  from: string;
  to: string;
  snapshots: ScoreSnapshot[];
}

/**
 * The Total + pillars of one point in a System's score history — all the
 * board's portfolio trend plots (feature 0056). A full ScoreSnapshot adds the
 * areas and metrics the Score tab drills into.
 */
export interface SlimScoreSnapshot {
  time: string;
  // A not-scored day carries a null overall (feature 0017): an all-unprofiled
  // System with no security data that day has nothing measured, so the overall
  // is null — NOT coerced to 0 (mirrors EntityScore + PillarSnapshot, and the
  // not-scored card/board semantics). The Total trend renders such a week as a
  // gap, never a red 0 dip.
  scorePercent: number | null;
  scoreSuccess: ScoreSuccessEnum | null;
  pillarScores: PillarSnapshot[];
}

export interface ScoreSnapshot extends SlimScoreSnapshot {
  areaScores: AreaSnapshot[];
  metricValues: MetricSnapshot[];
}

// --- Portfolio history (feature 0056) ---
// Mirrors IMC's AllSystemsScoreHistoryDto, served by both IMC and radar-backend
// at `<jsonDataUrl>history/all.json?from&to`: one latest-in-week snapshot per
// ISO week per System, areas/metrics omitted. `from`/`to` are formatted
// differently by the two sources (IMC: ISO timestamp, radar: YYYY-MM-DD) —
// nothing should read them.

export interface SystemScoreHistory {
  entityRef: CompoundEntityRef;
  snapshots: SlimScoreSnapshot[];
}

export interface PortfolioHistoryResponse {
  from: string;
  to: string;
  systems: SystemScoreHistory[];
}

/**
 * Every System's IMC + radar merged history, keyed by lower-cased
 * `entityRef.name` (radar names a project by its id, which is the System's
 * name). Snapshots are oldest first; a System with none is absent.
 */
export type PortfolioHistories = Record<string, SlimScoreSnapshot[]>;

export interface PillarSnapshot {
  id: number;
  key: string;
  title: string;
  scorePercent: number | null;
}

export interface AreaSnapshot {
  id: number;
  title: string;
  scorePercent: number;
  // Per-week finding counts, present only on Security-pillar areas (0014).
  // Additive; the weekly matrix renders these as Critical/High/Medium count
  // trend rows under the area (in lockstep with the IMC producer).
  critical?: number;
  high?: number;
  medium?: number;
}

export interface MetricSnapshot {
  id: number;
  key: string;
  value: number;
}
